import m, {MithrilComponent, type Children} from '@bitstillery/mithril'
import {classes} from '@bitstillery/common/lib/utils'

import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

export const heroBandMeta = {
    tier: 'layout',
    summary:
        'Full-bleed hero band over a background photo, with named content sections (title, body, aside, footer) and light text.',
    when_to_use:
        'Body copy goes as JSX children; a call-to-action is just a `<Button>` at the end of the body (no cta slot). A `background_image` is required and renders a full-bleed band with light text and the chosen photo treatment. Fill `aside` with a few `<Stat>` chips (side by side; drive one from an API with `<AsyncData>`) and `footer` with a strip like `<MarqueeStrip/>`.',
    example: `
    <HeroBand
        background_image="/common/img/catalog/example-band.jpg"
        title="Spirits trading, simplified"
        variant="washed"
    >
        <Paragraph variant="lead">One place to buy and sell verified stock.</Paragraph>
        <Button link="/offers" text="Browse offers" type="info" variant="cta" />
    </HeroBand>
    `,
    variants: [
        {
            prop: 'aside',
            jsx: `
            <HeroBand
                aside={<Stat label="Offers in stock" suffix="+" value={3500} />}
                background_image="/common/img/catalog/example-band.jpg"
                title="Spirits trading, simplified"
            >
                <Paragraph variant="lead">One place to buy and sell verified stock.</Paragraph>
            </HeroBand>
            `,
        },
        {
            prop: 'footer',
            jsx: `
            <HeroBand
                background_image="/common/img/catalog/example-band.jpg"
                footer={<MarqueeStrip items={['Whisky', 'Rum', 'Gin', 'Vodka', 'Cognac']} />}
                title="Spirits trading, simplified"
            >
                <Paragraph variant="lead">One place to buy and sell verified stock.</Paragraph>
            </HeroBand>
            `,
        },
    ],
} satisfies CmsComponentMeta

export interface HeroBandAttrs {
    /**
     * Required full-bleed background photo — a media library URL built as `${config.media_photo_host}/v5/<id>`
     * (`<id>` is the trailing segment of a `find_cms_media_files` `public_url`; same format as `Image`).
     * Pick an image that already frames well (no crop/position knob); the hero is always a full-bleed band
     * with light text over this photo.
     */
    background_image: string
    /**
     * Photo treatment: `washed` (default) — teal funnel photo-wash overlay; `greyscale` — desaturated photo;
     * `plain` — photo as-is.
     */
    variant?: 'washed' | 'greyscale' | 'plain'
    /** Optional minimum height for the band section (e.g. tall photo strips on marketing pages). */
    min_height?: string
    /** Heading. */
    title?: Children
    /** Secondary content area beside the body on wide viewports — laid out as a wrapping row (e.g. a few `<Stat>` chips). */
    aside?: Children
    /** Full-width strip pinned below the band (e.g. `<MarqueeStrip/>`). */
    footer?: Children
}

/**
 * Hero band with named content sections — `title`, body (children), `aside`, `footer` — filled by the
 * caller with text or existing components. Always a full-bleed band with light text over the required
 * `background_image` (photo treatment per `variant`). A call-to-action is just a `<Button>` at the end of
 * the body — there is no cta slot.
 */
export class HeroBand extends MithrilComponent<HeroBandAttrs> {
    view(vnode: m.Vnode<HeroBandAttrs>) {
        const variant = vnode.attrs.variant ?? 'washed'
        const min_height =
            typeof vnode.attrs.min_height === 'string' && vnode.attrs.min_height.trim() !== ''
                ? vnode.attrs.min_height.trim()
                : undefined
        /** Default slot — Mithril passes child vnodes here, not on `attrs.children`. */
        const body = vnode.children

        const style = {
            backgroundImage: `url("${vnode.attrs.background_image.trim()}")`,
            ...(min_height ? {minHeight: min_height} : {}),
        } as const

        return (
            <section className={classes('c-hero-band', variant)} style={style}>
                <div className='inner'>
                    <div className='body'>
                        {vnode.attrs.title ? <h3 className='title'>{vnode.attrs.title}</h3> : null}
                        {body ? <div className='text'>{body}</div> : null}
                    </div>
                    {vnode.attrs.aside ? <div className='aside'>{vnode.attrs.aside}</div> : null}
                </div>
                {vnode.attrs.footer ? <div className='footer'>{vnode.attrs.footer}</div> : null}
            </section>
        )
    }
}
